import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { ButtonPadraoComponent } from '../../../../shared/components/button-padrao/button-padrao.component';
import { FormFieldComponent } from '../../../../shared/components/form-field/form-field.component';
import { MsgService } from '../../../../shared/service/msg.service';
import { DIA_SEMANA_LABELS, DIAS_SEMANA, DiaSemana, HorarioFuncionamento } from '../../../../shared/model/horario-funcionamento/horario-funcionamento.model';
import { DiasSemanaComponent } from '../../../../shared/components/dias-semana/dias-semana.component';

@Component({
  selector: 'app-horario-funcionamento',
  standalone: true,
  imports: [
    CommonModule,
    ButtonPadraoComponent,
    FormFieldComponent,
    DiasSemanaComponent
],
  templateUrl: './horario-funcionamento.component.html',
  styleUrl: './horario-funcionamento.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorarioFuncionamentoComponent {

  @ViewChild('horaInicioInput') horaInicioInput!: FormFieldComponent;
  @ViewChild('horaFimInput') horaFimInput!: FormFieldComponent;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly msgService = inject(MsgService);

  @Input() horarios: HorarioFuncionamento[] = [];
  @Output() horariosChange = new EventEmitter<HorarioFuncionamento[]>();

  @Input() mensagemInatividadeFuncionamento = '';
  @Output() mensagemInatividadeFuncionamentoChange = new EventEmitter<string>();

  @Input() readOnly = false;

  modoCadastroAberto = false;
  horarioEmEdicaoIndex: number | null = null;
  diasSelecionados: DiaSemana[] = [];
  horaInicio = '';
  horaFim = '';

  get diasDisponiveis(): DiaSemana[] {
    const diasOcupados = new Set<DiaSemana>();

    this.horarios.forEach((horario, indice) => {
      if (indice === this.horarioEmEdicaoIndex) {
        return;
      }

      horario.dias.forEach(dia => diasOcupados.add(dia));
    });

    return DIAS_SEMANA.filter(dia => !diasOcupados.has(dia) || this.diasSelecionados.includes(dia));
  }

  get podeAdicionarFuncionamento(): boolean {
    return !this.readOnly && !this.modoCadastroAberto && this.diasDisponiveis.length > 0;
  }

  get deveMostrarHorarios(): boolean {
    return this.horarios.length > 0 && !this.modoCadastroAberto;
  }

  get deveMostrarMensagemInatividade(): boolean {
    return this.horarios.length > 0 && !this.modoCadastroAberto;
  }

  get textoBotaoAcao(): string {
    return this.horarioEmEdicaoIndex === null ? 'Adicionar Funcionamento' : 'Editar Funcionamento';
  }

  get podeSalvarHorario(): boolean {
    return (
      this.diasSelecionados.length > 0 &&
      !!this.horaInicio &&
      !!this.horaFim &&
      this.horaFim >= this.horaInicio
    );
  }

  get podeTentarSalvarHorario(): boolean {
    return this.diasSelecionados.length > 0 || !!this.horaInicio || !!this.horaFim;
  }

  adicionarFuncionamento(): void {
    this.modoCadastroAberto = true;
    this.horarioEmEdicaoIndex = null;
    this.diasSelecionados = [];
    this.horaInicio = '';
    this.horaFim = '';

    this.cdr.detectChanges();

    setTimeout(() => {
      this.horaInicioInput?.focus();
    });
  }

  editarHorario(indiceHorario: number): void {
    const horario = this.horarios[indiceHorario];
    if (!horario) {
      return;
    }

    this.horarioEmEdicaoIndex = indiceHorario;
    this.modoCadastroAberto = true;
    this.diasSelecionados = [...horario.dias];
    this.horaInicio = horario.horaInicio;
    this.horaFim = horario.horaFim;
  }

  deletarHorarios(indiceHorario: number): void {
    this.horarios.splice(indiceHorario, 1);
    this.horarios = [...this.horarios];
    this.horariosChange.emit([...this.horarios]);
  }

  confirmarFuncionamento(): void {
    if (!this.podeSalvarHorario) {
      this.msgService.msgAdvertencia(this.obterMensagemValidacao());
      return;
    }

    const novoHorario: HorarioFuncionamento = {
      dias: [...this.diasSelecionados],
      horaInicio: this.horaInicio,
      horaFim: this.horaFim
    };

    if (this.horarioEmEdicaoIndex === null) {
      this.horarios = [...this.horarios, novoHorario];
    } else {
      this.horarios = this.horarios.map((horario, indice) => indice === this.horarioEmEdicaoIndex ? novoHorario : horario);
    }

    this.horariosChange.emit([...this.horarios]);
    this.limparEdicao();
  }

  cancelarEdicao(): void {
    this.limparEdicao();
  }

  atualizarMensagemInatividadeFuncionamento(valor: string): void {
    this.mensagemInatividadeFuncionamento = valor;
    this.mensagemInatividadeFuncionamentoChange.emit(valor);
  }

  trackByHorario(index: number, horario: HorarioFuncionamento): string {
    return `${index}-${horario.horaInicio}-${horario.horaFim}-${horario.dias.join('-')}`;
  }

  obterLabelDia(dia: DiaSemana): string {
    return DIA_SEMANA_LABELS[dia];
  }

  private limparEdicao(): void {
    this.modoCadastroAberto = false;
    this.horarioEmEdicaoIndex = null;
    this.diasSelecionados = [];
    this.horaInicio = '';
    this.horaFim = '';
  }

  atualizarHoraInicio(valor: string): void {
    this.horaInicio = valor;
  }

  atualizarHoraFim(valor: string): void {
    this.horaFim = valor;
  }

  getMinumoHoraFim(): number{
    return parseInt(this.horaInicio)
  }

  private obterMensagemValidacao(): string {
    if (this.diasSelecionados.length === 0) {
      return 'Selecione ao menos um dia da semana.';
    }

    if (!this.horaInicio) {
      return 'Informe o horario inicial.';
    }

    if (!this.horaFim) {
      return 'Informe o horario final.';
    }

    if (this.horaFim < this.horaInicio) {
      return 'O horario final deve ser maior que o horario inicial.';
    }

    return 'Existem campos obrigatorios pendentes.';
  }

}
